/* ═══════════════════════════════════════════════════════════════
   NENSONS EXCHANGE BOARD — BRAND LAYER
   Assets > exchange-board-brand.js
     <script src="{{ 'exchange-board-brand.js' | asset_url }}" defer></script>

   Two independent pieces, each with its own guard flag:
     1. AMBIENT BRAND FIELD — scattered logo marks in the background.
     3. SNAPSHOT AUDIT / EDITOR — the price-anomaly inspector.

   Section 3 changes:
     v2 · ADD — fill a cell that has no data yet (the "—" columns), so a
          card can be given a price on a date where it never existed.
        · DELETE ONE — remove a card from a single snapshot.
        · DELETE ALL — remove a card, and everything about it, from every
          snapshot it appears in.
        All three are staff-only in the UI and must ALSO be enforced by
        the server; the client gate is convenience, not security.

     v3 · FIX: saveToBucket() called render() AFTER setSaveState(), and
          render() rewrites that same element with the pending count. The
          result was that every save outcome — success, rejection, error —
          flashed and was instantly replaced by "N sin guardar", so a save
          looked like it had never run. render() now goes first in every
          branch and the trailing call is gone.
        · The save button reports what it did even when nothing was sent,
          so a cancelled confirm no longer looks identical to a failure.
        · resetIndexStamps() after a clean save: the bucket file has been
          rewritten, so the frozen __i0 values describe a file that no
          longer exists and a second save would address the wrong rows.

     v4 · SKU is editable, across the whole row like name and image.
          The payload keeps `sku` as the value the server VERIFIES against
          (the one the bucket file has) and sends the new value as
          `new_sku`, because using one field for both would make the
          server reject its own edit. Duplicate skus are blocked in the
          browser as well as on the server.
        · Empty cells whose sku is already taken on that date show a ⚠
          instead of a "+", since the insert could only ever be refused.

     v5 · Index drift is handled at BOTH ends. The server treats `i` as a
          hint and re-finds the card by sku when the file has moved under
          us; this side drops its frozen indices after any save that wrote
          something, then re-reads the bucket, so the next payload is built
          against the file that now exists rather than the one we loaded.
        · Relocations the server had to make are shown in the footer and
          logged — silently correcting an index is the kind of help that
          hides a real problem.
   ═══════════════════════════════════════════════════════════════ */

/* ───────────────────────────────────────────────────────────────
   1. AMBIENT BRAND FIELD

   Scatters marks across the page background: each one its own size,
   angle and opacity. Background layers can't do this — one element
   takes one transform — so the field is built as real elements.

   Cost: one fixed container plus N small children, painted once and
   never touched again. No listeners, no animation, no work on
   scroll. Positions are percentages, so a resize needs no rebuild.

   If this script doesn't run, the CSS falls back to the static
   six-layer field in exchange-board-brand.css.
   ─────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.__nenFieldReady) return;
  window.__nenFieldReady = true;

  var LOGO = 'https://cdn.shopify.com/s/files/1/0731/4128/9172/files/NENSONS_LOGO.png?v=1776897699';

  function rnd(a, b) { return a + Math.random() * (b - a); }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  function build() {
    var page = document.querySelector('.dc-page');
    if (!page || page.querySelector('.nen-field')) return;

    var w = window.innerWidth || 1024;
    var count = w <= 640 ? 7 : (w <= 1100 ? 13 : 18);

    var field = document.createElement('div');
    field.className = 'nen-field';
    field.setAttribute('aria-hidden', 'true');

    // A jittered grid rather than free random. Unconstrained randomness
    // clumps — you reliably get four marks stacked in one corner and a
    // bare half-page. One mark per cell, jittered inside it, spreads the
    // field while still looking unplanned.
    var cols = Math.max(2, Math.round(Math.sqrt(count * 1.5)));
    var rows = Math.ceil(count / cols);
    var slots = [];
    for (var r = 0; r < rows; r++) {
      for (var c = 0; c < cols; c++) slots.push([c, r]);
    }
    shuffle(slots);
    slots = slots.slice(0, count);

    slots.forEach(function (s, i) {
      // one mark in five is a large one — the size contrast is what
      // stops the field reading as a repeating pattern
      var big = (i % 5 === 0);
      var size = Math.round(big ? rnd(230, 420) : rnd(64, 185));

      // angles cluster away from 0 so the marks look strewn rather than
      // laid out, but never so far that the logo reads upside down
      var rot = rnd(0, 1) < .5 ? rnd(-44, -7) : rnd(7, 44);

      // bigger marks go fainter, or they dominate the page
      var op = big ? rnd(.016, .028) : rnd(.022, .05);

      var x = ((s[0] + rnd(.12, .88)) / cols) * 100;
      var y = ((s[1] + rnd(.12, .88)) / rows) * 100;

      var m = document.createElement('i');
      m.className = 'nen-mark';
      m.style.cssText =
        'left:' + x.toFixed(2) + '%;' +
        'top:' + y.toFixed(2) + '%;' +
        'width:' + size + 'px;' +
        'height:' + size + 'px;' +
        'opacity:' + op.toFixed(3) + ';' +
        'transform:translate(-50%,-50%) rotate(' + rot.toFixed(1) + 'deg);';
      field.appendChild(m);
    });

    // first child, and the CSS keeps it at z-index 0 — everything else
    // in .dc-page sits at z-index 1 and stays in front
    page.insertBefore(field, page.firstChild);

    // tells the stylesheet to drop its static fallback field
    page.classList.add('nen-field-on');
  }

  // the image is already warm: the header loads the same URL
  var pre = new Image();
  pre.src = LOGO;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build, { once: true });
  } else {
    build();
  }
})();


/* ───────────────────────────────────────────────────────────────
   3. SNAPSHOT AUDIT / EDITOR

   Shows every card matching a query — by SKU, name, ticker or
   carta_num_id — with its price in EVERY snapshot side by side, so
   an impossible jump between two dates is visible at a glance.

   Editable:
     · price  — per snapshot (it IS a time series)
     · name   — across every snapshot the card appears in
     · image  — likewise

   Name and image are card metadata, not measurements. Editing one
   date's copy and leaving the others would fracture rowKey() and
   split the card into two rows, so both write across the whole row.

   STRUCTURAL EDITS (staff only)
     · ADD        — a "+" in an empty cell inserts the card into that
                    snapshot, seeded from the nearest earlier date.
     · DELETE ONE — removes the card from one snapshot.
     · DELETE ALL — removes the whole card object — price, name, image,
                    SKU, set, rarity, tags, everything — from every
                    snapshot in the row. No other card is touched.

   Deleting splices the card out of snapshots[si].cards, which shifts
   every __i after it. Two things protect against that:
     · __i0 is stamped on every card in a snapshot before its first
       structural edit, and THAT is what the payload sends — so the
       server always gets the index the file on the bucket actually
       has, not a locally-shifted one.
     · the dirty maps are re-keyed after each splice, so a pending
       price edit doesn't silently follow the wrong card.

   Reads window.__EB_STATE.snapshots (live getters, set inside the
   page template's IIFE). Edits mutate those objects in memory; they
   reach the bucket only through "Guardar en el bucket".
   ─────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.__nenAuditReady) return;
  window.__nenAuditReady = true;

  var SEP = '\u00A7';

  var THRESHOLD = 100;              // % jump considered suspicious
  var dirty = new Map();            // 'date§__i' -> original price
  var dirtyMeta = new Map();        // 'date§__i' -> { name, image }
  var addedRows = [];               // [{ si, date, card }]      inserted here
  var removedRows = [];             // [{ si, date, pos, i0, sku, card }]
  var lastQuery = '';
  var lastRows = [];                // rows from the last render, for handlers
  var isStaff = false;              // confirmed by the server, not assumed

  // Set by setSaveState(), cleared by any further edit. While it holds a
  // message, render() leaves the footer alone — otherwise a repaint wipes
  // the save result before it can be read.
  var saveMsg = null;

  // Special pricing rule editor state. Rules live in a separate backend file,
  // not inside snapshots, so they survive future uploads.
  var ruleViewSku = '';
  var ruleViewRow = null;
  var catalogViewSku = '';
  var catalogViewRow = null;
  var rulesCache = null;
  var RULES_BASE = 'https://us-central1-nensonssubastas-35510.cloudfunctions.net/api/exchange/rules';

  function EB() { return window.__EB_STATE || null; }
  function snaps() { var s = EB(); return s ? s.snapshots : []; }

  var esc = function (s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  // Audit prices are shown with thousands separators while preserving 2 decimals.
  // Inputs stay editable as text; save parsing strips the commas back out.
  function formatAuditPrice(n) {
    var v = Number(n);
    if (!isFinite(v)) return '0.00';
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // carta_num_id may arrive as a real field or buried in tags — look in both,
  // so this keeps working once the Cloud Function starts emitting it.
  function cartaId(c) {
    var v = c.carta_num_id || c.cartaNumId || c['custom.carta_num_id'] || '';
    if (!v && Array.isArray(c.tags)) {
      for (var i = 0; i < c.tags.length; i++) {
        var t = String(c.tags[i]);
        if (/carta[_-]?num/i.test(t)) { v = t.split(/[:=]/).pop(); break; }
      }
    }
    return String(v || '').trim();
  }

  function haystack(c) {
    return [c.sku, c.ticker, c.name, cartaId(c)].join(' ').toLowerCase();
  }

  // Identity for grouping rows. Deliberately sku+name, NOT sku alone: when the
  // backend reassigns a SKU to a different card, that split is exactly what we
  // need to see rather than paper over.
  function rowKey(c) {
    return (c.sku || c.ticker || '') + SEP + (c.name || '');
  }

  function search(q) {
    var needle = String(q || '').trim().toLowerCase();
    if (!needle) return [];
    var rows = new Map();
    snaps().forEach(function (s, si) {
      s.cards.forEach(function (c) {
        if (haystack(c).indexOf(needle) < 0) return;
        var k = rowKey(c);
        if (!rows.has(k)) {
          rows.set(k, { sku: c.sku, ticker: c.ticker, name: c.name,
                        rarity: c.rarity, set: c.set, carta: cartaId(c),
                        image: c.image, cells: {} });
        }
        rows.get(k).cells[si] = c;      // the live card object — edits mutate it
      });
    });
    return [...rows.values()];
  }

  // % change between this cell and the nearest earlier filled cell in the row
  function deltaFor(row, si) {
    var cur = row.cells[si];
    if (!cur) return null;
    for (var j = si - 1; j >= 0; j--) {
      var prev = row.cells[j];
      if (prev && prev.price > 0) {
        return { pct: ((cur.price - prev.price) / prev.price) * 100, from: j };
      }
    }
    return null;
  }

  // is any cell in this row carrying an unexported metadata edit?
  function rowMetaDirty(row) {
    var S = snaps();
    for (var si = 0; si < S.length; si++) {
      var c = row.cells[si];
      if (c && dirtyMeta.has(S[si].date + SEP + c.__i)) return true;
    }
    return false;
  }

  function isAdded(card) {
    for (var i = 0; i < addedRows.length; i++) if (addedRows[i].card === card) return true;
    return false;
  }

  /* ─── STRUCTURAL EDIT PLUMBING ──────────────────────────────── */

  // The index the file on the bucket has, frozen before we start moving
  // cards around locally. Stamped once per snapshot, on its first
  // structural edit; buildIndex() in the template rewrites __i on every
  // render but never touches __i0.
  function stampOrig(si) {
    var s = snaps()[si];
    if (!s || s.__nenOrigStamped) return;
    s.cards.forEach(function (c, i) { if (c.__i0 == null) c.__i0 = i; });
    s.__nenOrigStamped = true;
  }

  function origIdx(card) {
    return card.__i0 != null ? card.__i0 : card.__i;
  }

  // Local positions only. The template's buildIndex() does the same thing
  // on its next render; this keeps our own table correct in the meantime.
  function reindex(si) {
    var s = snaps()[si];
    if (!s) return;
    s.cards.forEach(function (c, i) { c.__i = i; });
  }

  // Called once the bucket file has been rewritten. The frozen indices now
  // describe a file that no longer exists, and the drift is invisible from
  // here — it only surfaces as "sku no coincide" on the NEXT save. Local __i
  // already matches the new order (we applied the same splices the server
  // did), so dropping the stamps lets the next structural edit re-freeze
  // against the file as it now stands.
  function resetIndexStamps() {
    snaps().forEach(function (s) {
      delete s.__nenOrigStamped;
      (s.cards || []).forEach(function (c) { delete c.__i0; });
    });
  }

  // A splice at `pos` moves everything after it down one. The dirty maps are
  // keyed by position, so they have to move with it or a pending price edit
  // ends up pointing at a neighbouring card.
  function shiftKeysAfterDelete(map, date, pos) {
    var next = new Map();
    map.forEach(function (v, k) {
      var p = k.split(SEP);
      if (p[0] !== date) { next.set(k, v); return; }
      var i = +p[1];
      if (i === pos) return;                              // the card that just left
      next.set(i > pos ? date + SEP + (i - 1) : k, v);
    });
    return next;
  }

  // The price to seed a new cell with: the nearest earlier date that has one,
  // else the nearest later one, else 0. Same idea as carried_forward, but it
  // is marked price_source:'manual' so nobody mistakes it for a scrape.
  function seedPrice(row, si) {
    for (var j = si - 1; j >= 0; j--) if (row.cells[j] && row.cells[j].price > 0) return row.cells[j].price;
    var S = snaps();
    for (var k = si + 1; k < S.length; k++) if (row.cells[k] && row.cells[k].price > 0) return row.cells[k].price;
    return 0;
  }

  // Any filled cell in the row — the template for a card we're about to create.
  function templateCard(row) {
    var S = snaps();
    for (var i = 0; i < S.length; i++) if (row.cells[i]) return row.cells[i];
    return null;
  }

  function addCell(row, si) {
    var s = snaps()[si];
    if (!s || row.cells[si]) return;
    var t = templateCard(row);
    if (!t) return;

    stampOrig(si);

    var card = {
      ticker: t.ticker || '',
      name: t.name || '',
      price: seedPrice(row, si),
      set: t.set || '',
      color: t.color || '',
      rarity: t.rarity || '',
      image: t.image || '',
      qty: 0,
      tags: (t.tags || []).slice(),
      sku: t.sku || '',
      store_price_mxn: null,
      is_exception: false,
      price_source: 'manual',
      scraped_usd: null,
      tcgplayer_url: t.tcgplayer_url || '',
      handle: t.handle || '',
      set_display: t.set_display || '',
      in_stock: false
    };

    s.cards.push(card);
    card.__i = s.cards.length - 1;
    card.__i0 = null;                      // no index on the bucket yet — it's an insert
    addedRows.push({ si: si, date: s.date, card: card });
    saveMsg = null;
  }

  function deleteCell(row, si, opts) {
    var s = snaps()[si];
    var card = row.cells[si];
    if (!s || !card) return;

    var pos = s.cards.indexOf(card);
    if (pos < 0) return;

    // Removing something we added here is a plain undo — it never reached
    // the bucket, so there is nothing for the server to delete.
    var addAt = -1;
    for (var i = 0; i < addedRows.length; i++) {
      if (addedRows[i].card === card) { addAt = i; break; }
    }

    stampOrig(si);
    var i0 = origIdx(card);
    // Read this BEFORE the splice: shiftKeysAfterDelete drops this card's
    // dirtyMeta entry, and with it the only record of its original sku.
    var sku0 = origSku(s.date, card);

    s.cards.splice(pos, 1);

    if (addAt >= 0) {
      addedRows.splice(addAt, 1);
    } else {
      removedRows.push({ si: si, date: s.date, pos: pos, i0: i0, sku: sku0, card: card });
    }

    dirty = shiftKeysAfterDelete(dirty, s.date, pos);
    dirtyMeta = shiftKeysAfterDelete(dirtyMeta, s.date, pos);
    reindex(si);
    saveMsg = null;

    if (!opts || !opts.quiet) { render(); repaintBoard(); }
  }

  // Every trace of one card, everywhere. Walks newest-first so each splice
  // can't disturb a snapshot index still to be visited.
  function deleteRow(row) {
    var S = snaps();
    for (var si = S.length - 1; si >= 0; si--) {
      if (row.cells[si]) deleteCell(row, si, { quiet: true });
    }
    render();
    repaintBoard();
  }

  /* ─── STAFF GATE ────────────────────────────────────────────── */
  // The role is the server's call (STAFF_EMAILS), never the client's. A valid
  // token is not enough — the session route hands sellers one too. Sticky once
  // confirmed, because the token itself self-nulls after three minutes and an
  // audit session outlives that.
  async function confirmStaff() {
    if (isStaff) return true;
    if (typeof window.fetchStaffToken !== 'function') return false;
    try {
      var t = await window.fetchStaffToken();
      if (t && window.SESSION_ROLE === 'staff') { isStaff = true; return true; }
    } catch (_) { /* silencio */ }
    return false;
  }

  async function ruleAuthUrl() {
    if (typeof window.fetchStaffToken !== 'function') throw new Error('sin sesión de staff');
    var token = await window.fetchStaffToken();
    if (!token || window.SESSION_ROLE !== 'staff') throw new Error('no autorizado');
    return RULES_BASE + '?' + new URLSearchParams({
      market: 'one-piece', viewer: window.CUSTOMER_EMAIL || '', token: token
    });
  }

  async function fetchRules(force) {
    if (rulesCache && !force) return rulesCache;
    var url = await ruleAuthUrl();
    var res = await fetch(url, { cache:'no-store' });
    var data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'no se pudieron cargar reglas');
    rulesCache = data;
    return data;
  }

  function ruleForSku(data, sku) {
    var key = String(sku || '').trim().replace(/\s+/g, ' ').toUpperCase();
    return data && data.rules ? (data.rules[key] || null) : null;
  }

  function ruleViewHTML(row, data, loading, error) {
    var rule = ruleForSku(data, row.sku);
    var type = rule ? rule.type : 'none';
    var amount = rule && rule.value_mxn != null ? Number(rule.value_mxn).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}) : '';
    var note = rule ? (rule.note || '') : '';
    var legacy = data && Array.isArray(data.legacy_exception_skus) && data.legacy_exception_skus.some(function(x){
      return String(x).replace(/\s+/g,'').toUpperCase() === String(row.sku||'').replace(/\s+/g,'').toUpperCase();
    });
    var art = row.image ? '<img class="nen-au-ruleart" src="'+esc(row.image)+'" alt="">' : '<div class="nen-au-ruleart ph"></div>';
    return '<div class="nen-au-ruleview">' +
      '<aside class="nen-au-rulecard">'+art+
        '<div class="nen-au-rulecard-name">'+esc(row.name||'—')+'</div>'+
        '<div class="nen-au-rulecard-sku">'+esc(row.sku||row.ticker||'')+'</div>'+
        (legacy ? '<div class="nen-au-rulelegacy">Legacy exception SKU</div>' : '')+
        '<button type="button" class="nen-au-ruleback">← Volver a precios</button>'+
      '</aside>'+
      '<section class="nen-au-rulepanel">'+
        '<div class="nen-au-ruletitle">Special Rule</div>'+
        '<p class="nen-au-rulehelp">Esta regla se guarda por SKU y se aplicará automáticamente a los próximos snapshots.</p>'+
        (loading ? '<div class="nen-au-rulestatus">Cargando…</div>' : '')+
        (error ? '<div class="nen-au-rulestatus err">'+esc(error)+'</div>' : '')+
        '<label>Tipo de regla</label>'+
        '<select id="nenAuRuleType" '+(loading?'disabled':'')+'>'+
          '<option value="none" '+(type==='none'?'selected':'')+'>Sin regla</option>'+
          '<option value="floor" '+(type==='floor'?'selected':'')+'>Precio mínimo / piso</option>'+
          '<option value="fixed" '+(type==='fixed'?'selected':'')+'>Precio fijo</option>'+
        '</select>'+
        '<label>Monto MXN</label>'+
        '<input id="nenAuRuleValue" type="text" inputmode="decimal" value="'+esc(amount)+'" placeholder="Ej. 1,500.00" '+(type==='none'?'disabled':'')+'>'+
        '<div class="nen-au-ruleexample" id="nenAuRuleExample"></div>'+
        '<label>Nota (opcional)</label>'+
        '<textarea id="nenAuRuleNote" rows="3" placeholder="Por qué existe esta regla…" '+(loading?'disabled':'')+'>'+esc(note)+'</textarea>'+
        '<div class="nen-au-ruleactions">'+
          '<button type="button" id="nenAuRuleSave" class="nen-au-btn gold" '+(loading?'disabled':'')+'>Guardar Special Rule</button>'+
          '<span id="nenAuRuleMsg" class="nen-au-rulemsg"></span>'+
        '</div>'+
      '</section>'+
    '</div>';
  }

  function paintRuleExample() {
    var type = document.getElementById('nenAuRuleType');
    var value = document.getElementById('nenAuRuleValue');
    var out = document.getElementById('nenAuRuleExample');
    if (!type || !value || !out) return;
    value.disabled = type.value === 'none';
    if (type.value === 'floor') out.textContent = 'Ejemplo: si el precio calculado es $900 y el piso es $1,500, el snapshot guardará $1,500.';
    else if (type.value === 'fixed') out.textContent = 'Ejemplo: el snapshot guardará siempre este monto, sin importar el precio calculado.';
    else out.textContent = 'La carta seguirá el precio normal del snapshot.';
  }

  async function saveRuleView() {
    if (!ruleViewRow) return;
    var msg = document.getElementById('nenAuRuleMsg');
    var type = document.getElementById('nenAuRuleType').value;
    var raw = document.getElementById('nenAuRuleValue').value.replace(/,/g,'').trim();
    var note = document.getElementById('nenAuRuleNote').value.trim();
    var rule = null;
    if (type !== 'none') {
      var n = Number(raw);
      if (!Number.isFinite(n) || n < 0) { if (msg) { msg.textContent='Monto inválido'; msg.classList.add('err'); } return; }
      rule = { type:type, value_mxn:n, note:note, enabled:true };
    }
    try {
      if (msg) { msg.textContent='Guardando…'; msg.classList.remove('err'); }
      var url = await ruleAuthUrl();
      var res = await fetch(url, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({sku:ruleViewRow.sku, rule:rule}) });
      var data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'no se pudo guardar');
      rulesCache = null;
      var fresh = await fetchRules(true);
      if (msg) msg.textContent = data.removed ? '✔ Regla eliminada' : '✔ Regla guardada';
      var host = document.getElementById('nenAuBody');
      if (host) host.innerHTML = ruleViewHTML(ruleViewRow, fresh, false, '');
      bindRuleView();
    } catch (err) {
      if (msg) { msg.textContent = 'Error: '+err.message; msg.classList.add('err'); }
    }
  }

  function bindRuleView() {
    var back = document.querySelector('.nen-au-ruleback');
    if (back) back.onclick = function(){ ruleViewSku=''; ruleViewRow=null; render(); };
    var type = document.getElementById('nenAuRuleType');
    if (type) type.onchange = paintRuleExample;
    var save = document.getElementById('nenAuRuleSave');
    if (save) save.onclick = saveRuleView;
    paintRuleExample();
  }

  async function openRuleView(row) {
    ruleViewSku = row.sku || row.ticker || '';
    ruleViewRow = row;
    var host = document.getElementById('nenAuBody');
    if (!host) return;
    host.innerHTML = ruleViewHTML(row, rulesCache, true, '');
    bindRuleView();
    try {
      var data = await fetchRules(false);
      if (ruleViewRow !== row) return;
      host.innerHTML = ruleViewHTML(row, data, false, '');
      bindRuleView();
    } catch (err) {
      host.innerHTML = ruleViewHTML(row, null, false, err.message);
      bindRuleView();
    }
  }


  // ─── CANONICAL SHOPIFY + JSON CATALOG EDITOR ────────────────
  // Lives INSIDE the Auditar precios tool. This edits catalog identity/metadata,
  // not historical market observations. The backend updates Shopify first and
  // then propagates the confirmed catalog change through every matching snapshot.
  var PRODUCT_UPDATE_BASE = 'https://us-central1-nensonssubastas-35510.cloudfunctions.net/api/exchange/product-update';

  function catalogCard(row) {
    return templateCard(row) || {};
  }

  function tagsText(tags) {
    return Array.isArray(tags) ? tags.join(', ') : String(tags || '');
  }

  function catalogViewHTML(row, msg, isErr, busy) {
    var c = catalogCard(row);
    var art = c.image || row.image
      ? '<img class="nen-au-ruleart" src="'+esc(c.image || row.image)+'" alt="">'
      : '<div class="nen-au-ruleart ph"></div>';

    return '<div class="nen-au-ruleview nen-au-catalogview">' +
      '<aside class="nen-au-rulecard">' + art +
        '<div class="nen-au-rulecard-name">'+esc(c.name || row.name || '—')+'</div>'+
        '<div class="nen-au-rulecard-sku">'+esc(c.sku || row.sku || row.ticker || '')+'</div>'+
        '<button type="button" class="nen-au-catalogback">← Volver a precios</button>'+
      '</aside>'+
      '<section class="nen-au-rulepanel">'+
        '<div class="nen-au-ruletitle">Edición de catálogo · Staff</div>'+
        '<p class="nen-au-rulehelp">Actualiza el producto real en Shopify y sincroniza los datos de catálogo en todos los JSON históricos. Los precios, cantidades y demás valores históricos no se sobrescriben.</p>'+
        '<label>Nombre / título</label>'+
        '<input id="nenCatName" type="text" value="'+esc(c.name || row.name || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>SKU</label>'+
        '<input id="nenCatSku" type="text" value="'+esc(c.sku || row.sku || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>Carta # / ticker</label>'+
        '<input id="nenCatTicker" type="text" value="'+esc(c.ticker || row.ticker || row.carta || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>Tags de Shopify</label>'+
        '<textarea id="nenCatTags" rows="4" '+(busy?'disabled':'')+'>'+esc(tagsText(c.tags))+'</textarea>'+
        '<label>Color</label>'+
        '<input id="nenCatColor" type="text" value="'+esc(c.color || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>Handle</label>'+
        '<input id="nenCatHandle" type="text" value="'+esc(c.handle || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>Imagen principal</label>'+
        '<input id="nenCatImage" type="url" value="'+esc(c.image || row.image || '')+'" '+(busy?'disabled':'')+'>'+
        '<label>TCGplayer URL</label>'+
        '<input id="nenCatTcg" type="url" value="'+esc(c.tcgplayer_url || '')+'" '+(busy?'disabled':'')+'>'+
        '<div class="nen-au-ruleactions">'+
          '<button type="button" id="nenCatSave" class="nen-au-btn gold" '+(busy?'disabled':'')+'>Guardar en Shopify + JSON</button>'+
          '<span id="nenCatMsg" class="nen-au-rulemsg'+(isErr?' err':'')+'">'+esc(msg || '')+'</span>'+
        '</div>'+
      '</section>'+
    '</div>';
  }

  function bindCatalogView() {
    var back = document.querySelector('.nen-au-catalogback');
    if (back) back.onclick = function(){ catalogViewSku=''; catalogViewRow=null; render(); };
    var save = document.getElementById('nenCatSave');
    if (save) save.onclick = saveCatalogView;
  }

  function openCatalogView(row) {
    if (!isStaff || !row) return;
    ruleViewSku = ''; ruleViewRow = null;
    catalogViewSku = row.sku || row.ticker || '';
    catalogViewRow = row;
    var host = document.getElementById('nenAuBody');
    if (host) {
      host.innerHTML = catalogViewHTML(row, '', false, false);
      bindCatalogView();
    }
  }

  function readCatalogForm() {
    return {
      name: String(document.getElementById('nenCatName')?.value || '').trim(),
      sku: String(document.getElementById('nenCatSku')?.value || '').trim(),
      ticker: String(document.getElementById('nenCatTicker')?.value || '').trim(),
      tags: String(document.getElementById('nenCatTags')?.value || '').split(',').map(function(x){ return x.trim(); }).filter(Boolean),
      color: String(document.getElementById('nenCatColor')?.value || '').trim(),
      handle: String(document.getElementById('nenCatHandle')?.value || '').trim(),
      image: String(document.getElementById('nenCatImage')?.value || '').trim(),
      tcgplayer_url: String(document.getElementById('nenCatTcg')?.value || '').trim()
    };
  }

  async function saveCatalogView() {
    if (!catalogViewRow || !isStaff) return;
    var row = catalogViewRow;
    var c = catalogCard(row);
    var oldSku = String(c.sku || row.sku || '').trim();
    var next = readCatalogForm();
    if (!oldSku) return;
    if (!next.name || !next.sku || !next.ticker) {
      var bad = document.getElementById('nenCatMsg');
      if (bad) { bad.textContent = 'Nombre, SKU y carta # son obligatorios.'; bad.classList.add('err'); }
      return;
    }

    var changes = {};
    if (next.name !== String(c.name || row.name || '').trim()) changes.name = next.name;
    if (next.sku !== oldSku) changes.new_sku = next.sku;
    if (next.ticker !== String(c.ticker || row.ticker || '').trim()) changes.ticker = next.ticker;
    if (tagsText(next.tags) !== tagsText(c.tags)) changes.tags = next.tags;
    if (next.color !== String(c.color || '').trim()) changes.color = next.color;
    if (next.handle !== String(c.handle || '').trim()) changes.handle = next.handle;
    if (next.image !== String(c.image || row.image || '').trim()) changes.image = next.image;
    if (next.tcgplayer_url !== String(c.tcgplayer_url || '').trim()) changes.tcgplayer_url = next.tcgplayer_url;

    if (!Object.keys(changes).length) {
      var none = document.getElementById('nenCatMsg');
      if (none) { none.textContent = 'Sin cambios.'; none.classList.remove('err'); }
      return;
    }

    if (!window.confirm('Esto actualizará el producto real en Shopify y los datos de catálogo del mismo producto en todos los snapshots JSON.\n\n¿Continuar?')) return;

    var host = document.getElementById('nenAuBody');
    if (host) { host.innerHTML = catalogViewHTML(row, 'Guardando…', false, true); bindCatalogView(); }

    try {
      var token = await window.fetchStaffToken();
      if (!token || window.SESSION_ROLE !== 'staff') throw new Error('no autorizado');
      var url = PRODUCT_UPDATE_BASE + '?' + new URLSearchParams({
        market:'one-piece', viewer:window.CUSTOMER_EMAIL || '', token:token
      });
      var res = await fetch(url, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ sku:oldSku, changes:changes })
      });
      var data = {};
      try { data = await res.json(); } catch (_) {}
      if (!res.ok || !data.success) throw new Error(data.message || ('HTTP '+res.status));

      var newSku = data.new_sku || next.sku || oldSku;
      if (typeof window.refreshFromGCS === 'function') await window.refreshFromGCS();
      lastQuery = newSku;
      catalogViewSku = '';
      catalogViewRow = null;
      render();
      setSaveState('✔ Catálogo sincronizado · '+(data.cards_updated || 0)+' registros en '+(data.snapshots_updated || 0)+' snapshots', 'ok');
    } catch (err) {
      console.error('[audit catalog] save failed', err);
      if (host) { host.innerHTML = catalogViewHTML(row, 'Error: '+err.message, true, false); bindCatalogView(); }
    }
  }

  /* ─── TABLE ─────────────────────────────────────────────────── */

  function tableHTML(rows) {
    var S = snaps();
    if (!rows.length) {
      return '<div class="nen-au-empty">Sin coincidencias. Busca por SKU, nombre, carta # o carta_num_id.</div>';
    }

    // DISPLAY ORDER ONLY: newest snapshot first, immediately beside the sticky
    // card-info column. Keep the original snapshot index (`si`) attached so
    // editing/deleting still targets the correct bucket file.
    var displayS = S.map(function (s, si) {
      return { s: s, si: si };
    }).reverse();

    var head = '<th class="nen-au-idc">Carta</th>' + displayS.map(function (x) {
      return '<th>' + esc(x.s.date) + '</th>';
    }).join('');

    var body = rows.map(function (row, ri) {
      var cells = displayS.map(function (x) {
        var s = x.s;
        var si = x.si;
        var c = row.cells[si];

        // ── empty cell: offer to create the card on this date ──
        if (!c) {
          if (!isStaff) return '<td class="nen-au-void">—</td>';
          // Another row already holds this sku on this date. Inserting would
          // put two cards with one sku in one snapshot — refused by the
          // server, and the thing that splits a card in every view that
          // groups by sku. Say so here instead of after the round trip.
          var mySku = String(row.sku || '');
          var taken = mySku && (s.cards || []).some(function (x) {
            return String(x.sku || '') === mySku;
          });
          if (taken) {
            return '<td class="nen-au-void nen-au-clash" title="' + esc(mySku) +
              ' ya existe en ' + esc(s.date) +
              ' en otra fila — cambia el SKU o el nombre para unirlas">&#9888;</td>';
          }
          return '<td class="nen-au-void">' +
            '<button type="button" class="nen-au-add" data-row="' + ri + '" data-si="' + si + '" ' +
              'title="Añadir esta carta a ' + esc(s.date) + '">+</button>' +
            '</td>';
        }

        var d = deltaFor(row, si);
        var flag = d && Math.abs(d.pct) >= THRESHOLD ? ' nen-au-flag' : '';
        var isDirty = dirty.has(s.date + SEP + c.__i) ? ' nen-au-dirty' : '';
        var isNew = isAdded(c) ? ' nen-au-new' : '';
        var dTxt = d ? (d.pct > 0 ? '+' : '') + d.pct.toFixed(1) + '%' : '';

        var del = isStaff
          ? '<button type="button" class="nen-au-del" data-row="' + ri + '" data-si="' + si + '" ' +
              'title="Eliminar esta carta de ' + esc(s.date) + '" aria-label="Eliminar de ' + esc(s.date) + '">✕</button>'
          : '';

        return '<td class="nen-au-cell' + flag + isDirty + isNew + '">' +
          del +
          '<input type="text" inputmode="decimal" class="nen-au-px" value="' + formatAuditPrice(c.price) + '" ' +
            'data-si="' + si + '" data-i="' + c.__i + '" data-row="' + ri + '">' +
          (dTxt ? '<span class="nen-au-d ' + (d.pct > 0 ? 'up' : 'down') + '">' + dTxt + '</span>' : '') +
          (isNew ? '<span class="nen-au-newtag">nueva</span>' : '') +
          '</td>';
      }).join('');

      // The thumbnail is the toggle for the metadata fields — click it to
      // reveal the name and image inputs.
      var artImg = row.image
        ? '<img class="nen-au-th" src="' + esc(row.image) + '" alt="" loading="lazy" ' +
            'data-row="' + ri + '" title="Editar nombre e imagen">'
        : '<span class="nen-au-th nen-au-th-ph" data-row="' + ri + '" title="Editar nombre e imagen"></span>';
      var art = '<div class="nen-au-artcol">' + artImg +
        '<button type="button" class="nen-au-rulebtn" data-row="' + ri + '">Special Rule</button>' +
        (isStaff ? '<button type="button" class="nen-au-catalogbtn" data-row="' + ri + '">Edición de catálogo · Staff</button>' : '') +
        '</div>';

      var metaDirty = rowMetaDirty(row) ? ' nen-au-metadirty' : '';

      var rowDel = isStaff
        ? '<button type="button" class="nen-au-delrow" data-row="' + ri + '">' +
            '🗑 Eliminar esta carta por completo</button>'
        : '';

      return '<tr>' +
        '<td class="nen-au-idc' + metaDirty + '">' + art +
          '<div class="nen-au-idt">' +
            '<div class="nen-au-nm">' + esc(row.name || '—') + '</div>' +
            '<div class="nen-au-meta">' + esc(row.sku || row.ticker || '') +
              (row.rarity ? ' · ' + esc(row.rarity) : '') +
              (row.carta ? ' · id ' + esc(row.carta) : '') + '</div>' +
            '<div class="nen-au-edit" data-editrow="' + ri + '" hidden>' +
              '<input type="text" class="nen-au-sku" data-row="' + ri + '" ' +
                'placeholder="SKU (Variant SKU)" value="' + esc(row.sku || '') + '">' +
              '<input type="text" class="nen-au-name" data-row="' + ri + '" ' +
                'placeholder="Nombre de la carta" value="' + esc(row.name || '') + '">' +
              '<input type="url" class="nen-au-img" data-row="' + ri + '" ' +
                'placeholder="https://… (URL de la imagen)" value="' + esc(row.image || '') + '">' +
              rowDel +
            '</div>' +
          '</div>' +
        '</td>' + cells + '</tr>';
    }).join('');

    return '<div class="nen-au-scroll-shell">' +
      '<div class="nen-au-scroll"><table class="nen-au-table">' +
        '<thead><tr>' + head + '</tr></thead><tbody>' + body + '</tbody></table></div>' +
      '<div class="nen-au-goldbar" aria-hidden="true"><div class="nen-au-goldthumb"><span class="nen-au-golddot"></span></div></div>' +
      '<div class="nen-au-goldpos" aria-hidden="true">0%</div>' +
    '</div>';
  }

  function pendingCount() {
    return dirty.size + dirtyMeta.size + addedRows.length + removedRows.length;
  }

  function pendingLabel() {
    var bits = [];
    var px = dirty.size + dirtyMeta.size;
    if (px) bits.push(px + ' ' + (px === 1 ? 'cambio' : 'cambios'));
    if (addedRows.length) bits.push(addedRows.length + ' ' + (addedRows.length === 1 ? 'añadida' : 'añadidas'));
    if (removedRows.length) bits.push(removedRows.length + ' ' + (removedRows.length === 1 ? 'eliminada' : 'eliminadas'));
    return bits.length ? bits.join(' · ') + ' sin guardar' : 'sin cambios';
  }

  // The footer belongs to the last save result until the next edit. Without
  // this, every render() after a save overwrote the outcome with the pending
  // count and the save looked like it had never happened.
  function paintFooter() {
    var d = document.getElementById('nenAuDirty');
    if (!d) return;
    if (saveMsg) {
      d.textContent = saveMsg.text;
      d.classList.toggle('on', saveMsg.cls !== 'ok');
      d.classList.toggle('err', saveMsg.cls === 'err');
      return;
    }
    d.textContent = pendingLabel();
    d.classList.remove('err');
    d.classList.toggle('on', pendingCount() > 0);
  }

  function render() {
    var rows = search(lastQuery);
    lastRows = rows;

    var host = document.getElementById('nenAuBody');
    if (catalogViewSku) {
      var cr = rows.find(function(r){ return String(r.sku||r.ticker||'') === catalogViewSku; }) || catalogViewRow;
      if (cr) { catalogViewRow = cr; if (host) { host.innerHTML = catalogViewHTML(cr, '', false, false); bindCatalogView(); } }
      else { catalogViewSku=''; catalogViewRow=null; if (host) host.innerHTML = tableHTML(rows); }
    } else if (ruleViewSku) {
      var rr = rows.find(function(r){ return String(r.sku||r.ticker||'') === ruleViewSku; }) || ruleViewRow;
      if (rr) { ruleViewRow = rr; if (host) { host.innerHTML = ruleViewHTML(rr, rulesCache, false, ''); bindRuleView(); } }
      else { ruleViewSku=''; ruleViewRow=null; if (host) host.innerHTML = tableHTML(rows); }
    } else if (host) host.innerHTML = tableHTML(rows);

    var cnt = document.getElementById('nenAuCount');
    if (cnt) {
      var flagged = 0;
      rows.forEach(function (r) {
        snaps().forEach(function (s, si) {
          var d = deltaFor(r, si);
          if (d && Math.abs(d.pct) >= THRESHOLD) flagged++;
        });
      });
      cnt.textContent = rows.length + ' cartas · ' + flagged + ' saltos > ' + THRESHOLD + '%';
    }

    paintFooter();

    var panel = document.querySelector('.nen-au-panel');
    if (panel) panel.classList.toggle('nen-au-staff', isStaff);
  }

  // The board's own render() lives inside a closure we can't call. Dispatching
  // change on the snapshot selector re-enters it with the same value, which
  // repaints every section against the edited data.
  function repaintBoard() {
    var sel = document.getElementById('snapSelect');
    if (sel) sel.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Remember a card's pre-edit metadata exactly once, so revert restores the
  // ORIGINAL rather than the value from the previous edit.
  function stashMeta(date, card) {
    var key = date + SEP + card.__i;
    if (!dirtyMeta.has(key)) {
      dirtyMeta.set(key, { name: card.name, image: card.image, sku: card.sku });
    }
  }

  // The sku the BUCKET file has — which is the value the server verifies the
  // index against. Once a sku has been edited, card.sku is the NEW value, and
  // sending that as the verification key would make the server reject the very
  // edit that changed it.
  function origSku(date, card) {
    var m = dirtyMeta.get(date + SEP + card.__i);
    return (m && m.sku != null) ? m.sku : (card.sku || '');
  }

  // ── SAVE TO BUCKET ──────────────────────────────────────────
  // The board loads price_mxn as `price`, so the wire format has to be
  // translated back on the way out. Every entry carries an `op` and the sku
  // the client believes sits at that index; the server rejects the edit if
  // the stored card disagrees, rather than writing to a neighbouring row.
  //
  //   op:'set'    { i, sku, price_mxn?, name?, image? }   patch in place
  //   op:'add'    { sku, ...full card }                   append to the snapshot
  //   op:'delete' { i, sku }                              remove from the snapshot
  //
  // `i` is ALWAYS the index the bucket file has (__i0), never the local one.
  // Deletes must be applied by the server in DESCENDING index order within a
  // date, or each removal invalidates the indices after it.
  function buildPayload() {
    var byDate = {};

    function bucket(date) {
      if (!byDate[date]) byDate[date] = { set: {}, add: [], del: {} };
      return byDate[date];
    }

    function slot(date, i, card) {
      var b = bucket(date).set;
      if (!b[i]) b[i] = { op: 'set', i: origIdx(card), sku: origSku(date, card) };
      return b[i];
    }

    dirty.forEach(function (orig, key) {
      var p = key.split(SEP);
      var s = snaps().find(function (x) { return x.date === p[0]; });
      if (!s) return;
      var card = s.cards[+p[1]];
      if (!card) return;
      if (isAdded(card)) return;                 // covered by its own add entry
      slot(p[0], p[1], card).price_mxn = card.price;
    });

    dirtyMeta.forEach(function (orig, key) {
      var p = key.split(SEP);
      var s = snaps().find(function (x) { return x.date === p[0]; });
      if (!s) return;
      var card = s.cards[+p[1]];
      if (!card) return;
      if (isAdded(card)) return;
      var e = slot(p[0], p[1], card);
      if (card.name !== orig.name) e.name = card.name;
      if (card.image !== orig.image) e.image = card.image;
      // `sku` above is the verification key; the replacement rides separately.
      if (orig.sku != null && card.sku !== orig.sku) e.new_sku = card.sku;
    });

    addedRows.forEach(function (rec) {
      var c = rec.card;
      bucket(rec.date).add.push({
        op: 'add',
        sku: c.sku || '', ticker: c.ticker, name: c.name, price_mxn: c.price,
        set: c.set, color: c.color, rarity: c.rarity, image: c.image,
        qty: c.qty, tags: c.tags, store_price_mxn: c.store_price_mxn,
        is_exception: c.is_exception, price_source: c.price_source,
        scraped_usd: c.scraped_usd, tcgplayer_url: c.tcgplayer_url,
        handle: c.handle, setDisplay: c.set_display, in_stock: c.in_stock
      });
    });

    removedRows.forEach(function (rec) {
      bucket(rec.date).del[rec.i0] = { op: 'delete', i: rec.i0, sku: rec.sku };
    });

    var out = {};
    Object.keys(byDate).forEach(function (d) {
      var b = byDate[d];
      var sets = Object.keys(b.set).map(function (k) { return b.set[k]; })
        // an entry with only op+i+sku is a no-op the server would reject
        .filter(function (e) { return Object.keys(e).length > 3; });
      // descending index, so applying them in order can't shift the next one
      var dels = Object.keys(b.del).map(function (k) { return b.del[k]; })
        .sort(function (a, z) { return z.i - a.i; });
      var list = sets.concat(b.add, dels);
      if (list.length) out[d] = list;
    });
    return out;
  }

  // Records the outcome AND paints it. render() will preserve it until the
  // next edit clears saveMsg.
  function setSaveState(msg, cls) {
    saveMsg = (cls === 'clear') ? null : { text: msg, cls: cls };
    var b = document.getElementById('nenAuSave');
    if (b) b.disabled = (cls === 'busy');
    paintFooter();
  }

  async function saveToBucket() {
    var edits = buildPayload();
    if (!Object.keys(edits).length) { setSaveState('sin cambios que guardar', 'ok'); return; }

    if (typeof window.fetchStaffToken !== 'function') {
      setSaveState('sin sesión de staff', 'err');
      return;
    }

    // Structural edits rewrite the card list, so make the scope explicit
    // before anything leaves the browser.
    if (addedRows.length || removedRows.length) {
      var parts = [];
      if (addedRows.length) parts.push(addedRows.length + ' carta(s) añadida(s)');
      if (removedRows.length) parts.push(removedRows.length + ' carta(s) eliminada(s)');
      if (!window.confirm(
        'Vas a guardar en el bucket:\n\n  · ' + parts.join('\n  · ') +
        '\n\nEsto reescribe las snapshots afectadas. ¿Continuar?')) {
        // Say so out loud: a silent return here is indistinguishable from a
        // save that ran and failed.
        setSaveState('guardado cancelado · ' + pendingLabel(), 'err');
        return;
      }
    }

    setSaveState('guardando…', 'busy');

    try {
      // Fetched per save, never cached: the page nulls the token after three
      // minutes, and an audit session easily outlives that.
      var token = await window.fetchStaffToken();
      if (!token) { setSaveState('no autorizado — recarga la página', 'err'); return; }
      if (window.SESSION_ROLE !== 'staff') { setSaveState('tu sesión no es de staff', 'err'); return; }

      var url = 'https://us-central1-nensonssubastas-35510.cloudfunctions.net/api/exchange/patch?' +
        new URLSearchParams({
          market: 'one-piece',
          viewer: window.CUSTOMER_EMAIL || '',
          token: token
        });

      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits: edits })
      });

      var data = {};
      try { data = await res.json(); } catch (_) { data = {}; }

      if (!res.ok || !data.success) {
        console.warn('[audit] patch falló', res.status, data);
        setSaveState('error ' + res.status + ': ' + (data.message || 'respuesta inesperada'), 'err');
        return;
      }

      // Only clear the dirty maps on a clean save. Anything the server refused
      // is still unsaved, and forgetting that would lose the correction
      // silently — the one failure mode this whole tool exists to prevent.
      //
      // render() FIRST in both branches: it repaints the footer, so setting
      // the message before it would erase the result.
      if (data.relocated && data.relocated.length) {
        console.warn('[audit] índices reubicados por el servidor:', data.relocated);
      }

      // ANY successful write rewrites the file, so the frozen indices stop
      // describing it — whether or not some entries were also refused.
      if (data.cards_patched) resetIndexStamps();

      var moved = (data.relocated || []).length;
      var movedTxt = moved ? ' · ' + moved + ' reubicada' + (moved === 1 ? '' : 's') : '';

      if (data.rejected && data.rejected.length) {
        console.warn('[audit] rechazados:', data.rejected);
        render();
        setSaveState((data.cards_patched || 0) + ' guardadas · ' +
                     data.rejected.length + ' rechazadas (ver consola)' + movedTxt, 'err');
        return;
      }

      dirty.clear();
      dirtyMeta.clear();
      addedRows.length = 0;
      removedRows.length = 0;
      render();
      setSaveState('✔ ' + (data.cards_patched || 0) + ' guardadas en el bucket' + movedTxt, 'ok');

      // Nothing is pending now, so re-reading the bucket costs nothing and
      // closes the last gap between the browser's copy and the file. Without
      // it the next save is built against a snapshot we mutated by hand and
      // merely BELIEVE matches what the server wrote.
      if (typeof window.refreshFromGCS === 'function') {
        var keep = saveMsg;
        window.refreshFromGCS()
          .then(function () { saveMsg = keep; render(); })
          .catch(function (e) {
            console.warn('[audit] recarga posterior al guardado falló', e);
            setSaveState('guardado · recarga la página para re-sincronizar', 'err');
          });
      }
    } catch (err) {
      console.error('[audit] save failed', err);
      setSaveState('error de conexión — nada se guardó', 'err');
    }
  }

  function download(name, text, mime) {
    var blob = new Blob([text], { type: mime || 'text/plain;charset=utf-8' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 400);
  }

  function csvCell(v) {
    var s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }

  function exportSnapshot(si, fmt) {
    var s = snaps()[si];
    if (!s) return;

    if (fmt === 'json') {
      // Mirrors the GCS snapshot shape, so this file can replace the object at
      // exchange/one-piece/snapshots/<date>.json directly. PREFER THIS: the
      // CSV path goes back through parseUploadedFile, which recomputes set,
      // rarity, modifiers and price_source from scratch.
      //
      // It also reflects adds and deletes, so this is the fallback route for
      // structural edits if the patch endpoint doesn't support them yet:
      // export the JSON, then re-upload it for that date.
      download(s.date + '.json', JSON.stringify({
        date: s.date,
        generated_at: new Date().toISOString(),
        rate: s.rate,
        stats: s.stats,
        cards: s.cards.map(function (c) {
          return {
            sku: c.sku, ticker: c.ticker, name: c.name, price_mxn: c.price,
            set: c.set, color: c.color, rarity: c.rarity, image: c.image,
            qty: c.qty, tags: c.tags, store_price_mxn: c.store_price_mxn,
            is_exception: c.is_exception, price_source: c.price_source,
            scraped_usd: c.scraped_usd, tcgplayer_url: c.tcgplayer_url,
            handle: c.handle, setDisplay: c.set_display, in_stock: c.in_stock
          };
        })
      }, null, 2), 'application/json');
      setSaveState('JSON de ' + s.date + ' descargado · súbelo con “⬆ Subir snapshot”', 'ok');
      return;
    }

    // Header names the upload parser's findKey() actually matches. Do not
    // rename: 'ticker' misses carta_num_id (ticker silently becomes the SKU),
    // and a missing Tags column makes deriveRarity fall back to the SKU
    // suffix alone, degrading rarity on every round-trip.
    var cols = [
      ['Variant SKU',           function (c) { return c.sku; }],
      ['carta_num_id',          function (c) { return c.ticker; }],
      ['Title',                 function (c) { return c.name; }],
      ['Variant Price',         function (c) { return c.price; }],
      ['Tags',                  function (c) { return (c.tags || []).join(', '); }],
      ['Variant Inventory Qty', function (c) { return c.qty; }],
      ['Handle',                function (c) { return c.handle; }],
      ['Image Src',             function (c) { return c.image; }],
      ['color-pattern',         function (c) { return c.color; }],
      ['tcgplayer url',         function (c) { return c.tcgplayer_url; }],
      ['scraped_usd',           function (c) { return c.scraped_usd; }]
    ];
    var lines = [cols.map(function (x) { return x[0]; }).join(',')];
    s.cards.forEach(function (c) {
      lines.push(cols.map(function (x) { return csvCell(x[1](c)); }).join(','));
    });
    // Filename MUST start with the date: the upload endpoint falls back to
    // TODAY when it can't parse one, which would create a new snapshot
    // instead of replacing the one being corrected.
    download(s.date + '-marketwatch-corregido.csv', lines.join('\n'), 'text/csv;charset=utf-8');
    setSaveState('CSV de ' + s.date + ' descargado', 'ok');
  }

  /* ─── STYLES FOR THE NEW CONTROLS ───────────────────────────── */
  // Injected rather than added to exchange-board-brand.css so this file is a
  // drop-in. Move it into the stylesheet whenever convenient.
  function injectCSS() {
    if (document.getElementById('nenAuCSS')) return;
    var st = document.createElement('style');
    st.id = 'nenAuCSS';
    st.textContent = [
      // Audit table: the card identity stays visible while dates scroll.
      // Hide the native scrollbar completely. The custom gold rail starts AFTER
      // the sticky 390px card column, so no scrollbar is ever drawn below Carta.
      '.nen-au-scroll-shell{position:relative;padding-bottom:15px}',
      '.nen-au-scroll{position:relative;overflow-x:auto;overflow-y:auto;cursor:grab;scroll-behavior:auto;',
      '  scrollbar-width:none;-ms-overflow-style:none}',
      '.nen-au-scroll::-webkit-scrollbar{display:none;width:0;height:0}',
      '.nen-au-goldbar{position:absolute;left:406px;right:58px;bottom:4px;height:7px;border-radius:999px;',
      '  background:rgba(239,196,65,.10);box-shadow:inset 0 0 0 1px rgba(239,196,65,.10);overflow:hidden;cursor:pointer}',
      '.nen-au-goldthumb{position:absolute;left:0;top:0;height:100%;min-width:34px;border-radius:999px;',
      '  background:linear-gradient(90deg,#B98714,#EFC441,#FFE88D);box-shadow:0 0 9px rgba(239,196,65,.25);',
      '  transform:translate3d(0,0,0);will-change:transform,width}',
      '.nen-au-golddot{position:absolute;right:2px;top:50%;width:9px;height:9px;border-radius:50%;transform:translateY(-50%);',
      '  background:#FFF1A8;box-shadow:0 0 0 2px rgba(239,196,65,.28),0 0 8px rgba(239,196,65,.6)}',
      '.nen-au-goldpos{position:absolute;right:10px;bottom:0px;width:42px;text-align:right;font:700 9px/15px ui-monospace,SFMono-Regular,Menlo,monospace;',
      '  color:#EFC441;letter-spacing:.04em;pointer-events:none}',
      '.nen-au-goldbar:hover .nen-au-goldthumb{filter:brightness(1.08)}',
      '.nen-au-scroll.nen-au-dragging{cursor:grabbing;user-select:none}',
      '.nen-au-scroll.nen-au-dragging *{cursor:grabbing!important;user-select:none!important}',
      '.nen-au-table{width:max-content;min-width:100%;border-collapse:separate;border-spacing:0}',
      '.nen-au-table .nen-au-idc{position:sticky;left:0;z-index:5;min-width:390px;width:390px;max-width:430px;',
      '  background:#0C121A;box-shadow:12px 0 22px rgba(0,0,0,.46);}',
      '.nen-au-table thead .nen-au-idc{z-index:8;background:#18222E}',
      '.nen-au-table tbody tr:hover .nen-au-idc{background:#111923}',
      // Bigger card art + more breathing room in the sticky identity column.
      '.nen-au-table .nen-au-th{width:92px!important;height:128px!important;min-width:92px;object-fit:cover;border-radius:8px;flex:0 0 92px}',
      '.nen-au-table .nen-au-idc{padding:14px 16px!important}',
      '.nen-au-table .nen-au-idt{min-width:245px}',
      '.nen-au-table .nen-au-nm{font-size:14px;line-height:1.3}',
      '.nen-au-table .nen-au-meta{font-size:11px;line-height:1.4}',
      // Wider date/price columns intentionally show fewer dates at once.
      // Older snapshots remain available by horizontal scrolling.
      '.nen-au-table th:not(.nen-au-idc),.nen-au-table td:not(.nen-au-idc){min-width:158px;width:158px;text-align:center}',
      '.nen-au-table .nen-au-px{width:132px;text-align:right;font-variant-numeric:tabular-nums}',
      '.nen-au-cell{position:relative}',
      '.nen-au-del{position:absolute;top:2px;right:2px;z-index:2;width:16px;height:16px;',
      '  padding:0;line-height:14px;font-size:10px;border-radius:50%;cursor:pointer;',
      '  background:rgba(255,91,107,.14);color:#ff8a95;border:1px solid rgba(255,91,107,.32);',
      '  opacity:0;transition:opacity .14s ease}',
      '.nen-au-cell:hover .nen-au-del,.nen-au-del:focus-visible{opacity:1}',
      '.nen-au-del:hover{background:rgba(255,91,107,.28)}',
      '.nen-au-add{width:26px;height:26px;padding:0;line-height:24px;font-size:15px;',
      '  border-radius:50%;cursor:pointer;background:transparent;color:#5F7488;',
      '  border:1px dashed rgba(122,152,180,.34);transition:all .14s ease}',
      '.nen-au-add:hover{color:#EFC441;border-color:rgba(239,196,65,.55);',
      '  background:rgba(239,196,65,.08)}',
      '.nen-au-new{outline:1px solid rgba(38,227,155,.45);outline-offset:-1px;',
      '  background:rgba(38,227,155,.06)}',
      '.nen-au-newtag{display:block;font-size:9px;letter-spacing:.08em;text-transform:uppercase;',
      '  color:#26E39B;margin-top:2px}',
      '.nen-au-delrow{display:block;width:100%;margin-top:6px;padding:5px 8px;font-size:11px;',
      '  cursor:pointer;border-radius:6px;background:rgba(255,91,107,.10);color:#ff8a95;',
      '  border:1px solid rgba(255,91,107,.28)}',
      '.nen-au-delrow:hover{background:rgba(255,91,107,.22)}',
      '.nen-au-sku{display:block;width:100%;margin-top:6px;padding:5px 8px;',
      '  font-size:11px;border-radius:6px;background:#121A24;color:#EFC441;',
      '  border:1px solid rgba(239,196,65,.30);letter-spacing:.02em;',
      '  font-family:\'JetBrains Mono\', ui-monospace, monospace}',
      '.nen-au-sku:focus{outline:none;border-color:rgba(239,196,65,.65)}',
      '.nen-au-clash{color:#EFC441;font-size:13px;cursor:help}',
      '.nen-au-artcol{display:flex;flex-direction:column;gap:7px;flex:0 0 92px;align-items:stretch}',
      '.nen-au-rulebtn{width:92px;padding:5px 4px;border-radius:7px;cursor:pointer;font-size:10px;font-weight:800;',
      '  background:rgba(239,196,65,.10);color:#EFC441;border:1px solid rgba(239,196,65,.34)}',
      '.nen-au-rulebtn:hover{background:rgba(239,196,65,.20)}',
      '.nen-au-catalogbtn{width:92px;padding:6px 4px;border-radius:7px;cursor:pointer;font-size:9px;line-height:1.2;font-weight:800;',
      '  background:rgba(38,227,155,.08);color:#61efb5;border:1px solid rgba(38,227,155,.28)}',
      '.nen-au-catalogbtn:hover{background:rgba(38,227,155,.17)}',
      '.nen-au-catalogback{margin-top:8px;background:#121A24;color:#93A7BA;border:1px solid rgba(122,152,180,.22);border-radius:8px;padding:8px 10px;cursor:pointer}',
      '.nen-au-catalogview textarea{min-height:86px;resize:vertical}',
      '.nen-au-ruleview{display:grid;grid-template-columns:minmax(190px,260px) minmax(0,1fr);gap:22px;padding:18px;min-height:420px}',
      '.nen-au-rulecard{padding:16px;border-right:1px solid rgba(122,152,180,.16);display:flex;flex-direction:column;align-items:center;gap:9px}',
      '.nen-au-ruleart{width:150px;aspect-ratio:5/7;object-fit:cover;border-radius:10px;background:#18222E}',
      '.nen-au-rulecard-name{text-align:center;font-weight:800;color:#fff;font-size:15px}',
      '.nen-au-rulecard-sku{font:600 11px JetBrains Mono,monospace;color:#EFC441}',
      '.nen-au-rulelegacy{font:700 10px JetBrains Mono,monospace;color:#ffb3ba;padding:4px 8px;border:1px solid rgba(255,91,107,.3);border-radius:999px}',
      '.nen-au-ruleback{margin-top:8px;background:#121A24;color:#93A7BA;border:1px solid rgba(122,152,180,.22);border-radius:8px;padding:8px 10px;cursor:pointer}',
      '.nen-au-rulepanel{max-width:620px;padding:8px 4px;display:flex;flex-direction:column;gap:8px}',
      '.nen-au-ruletitle{font-size:22px;font-weight:900;color:#fff}',
      '.nen-au-rulehelp,.nen-au-ruleexample{font-size:12px;color:#93A7BA;margin:0 0 8px}',
      '.nen-au-rulepanel label{font:700 10px JetBrains Mono,monospace;text-transform:uppercase;letter-spacing:.1em;color:#5F7488;margin-top:8px}',
      '.nen-au-rulepanel select,.nen-au-rulepanel input,.nen-au-rulepanel textarea{width:100%;background:#121A24;color:#E8EFF5;border:1px solid rgba(122,152,180,.24);border-radius:9px;padding:10px 12px;font:13px JetBrains Mono,monospace}',
      '.nen-au-ruleactions{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-top:10px}',
      '.nen-au-rulemsg{font:700 11px JetBrains Mono,monospace;color:#26E39B}.nen-au-rulemsg.err,.nen-au-rulestatus.err{color:#ff8a95}',
      '.nen-au-rulestatus{font-size:12px;color:#93A7BA}',
      '@media(max-width:700px){.nen-au-ruleview{grid-template-columns:1fr}.nen-au-rulecard{border-right:0;border-bottom:1px solid rgba(122,152,180,.16)}}',
      '.nen-au-dirty.err,.nen-au-dirty.err *{color:#ff8a95}'
    ].join('');
    document.head.appendChild(st);
  }

  function buildModal() {
    if (document.getElementById('nenAuBackdrop')) return;
    injectCSS();

    var S = snaps();
    var opts = S.map(function (s, i) {
      return '<option value="' + i + '">' + esc(s.date) + '</option>';
    }).join('');

    var el = document.createElement('div');
    el.className = 'nen-au-backdrop';
    el.id = 'nenAuBackdrop';
    el.innerHTML =
      '<div class="nen-au-panel" role="dialog" aria-modal="true" aria-label="Auditoría de snapshots">' +
        '<button class="nen-au-close" id="nenAuClose" type="button" aria-label="Cerrar">✕</button>' +
        '<div class="nen-au-head">' +
          '<h3>Auditoría de snapshots</h3>' +
          '<input type="search" id="nenAuQ" class="nen-au-q" placeholder="SKU, nombre, carta # o carta_num_id…">' +
          '<label class="nen-au-lbl">Umbral %</label>' +
          '<input type="number" id="nenAuTh" class="nen-au-th-in" value="' + THRESHOLD + '" step="10" min="1">' +
          '<span class="nen-au-count" id="nenAuCount"></span>' +
        '</div>' +
        '<div class="nen-au-body" id="nenAuBody"></div>' +
        '<div class="nen-au-foot">' +
          '<span class="nen-au-dirty" id="nenAuDirty">sin cambios</span>' +
          '<button class="nen-au-btn" id="nenAuRevert" type="button">↺ Deshacer todo</button>' +
          '<button class="nen-au-btn gold" id="nenAuSave" type="button">💾 Guardar en el bucket</button>' +
          '<select id="nenAuSnap" class="nen-au-sel">' + opts + '</select>' +
          '<button class="nen-au-btn" id="nenAuCsv" type="button">⬇ CSV</button>' +
          '<button class="nen-au-btn" id="nenAuJson" type="button">⬇ JSON</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      if (e.target === el) closeAudit();
    });
    document.getElementById('nenAuClose').onclick = closeAudit;

    var qEl = document.getElementById('nenAuQ');
    var t = null;
    qEl.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(function () { lastQuery = qEl.value; render(); }, 180);
    });

    document.getElementById('nenAuTh').addEventListener('input', function (e) {
      var n = parseFloat(e.target.value);
      THRESHOLD = (isNaN(n) || n <= 0) ? 100 : n;
      render();
    });

    var bodyEl = document.getElementById('nenAuBody');

    // ── mouse/pen drag-to-scroll for the snapshot price rail ──────────
    // EXACT motion engine copied from the main Exchange Board rails module.
    // Only audit-specific adaptations:
    //   1) inputs remain clickable until the same 4px drag threshold is crossed;
    //   2) the custom gold position rail is synchronized from this same loop.
    var AUDIT_LERP = 0.28;
    var AUDIT_FRICTION = 0.978;
    var AUDIT_MIN_VEL = 0.04;
    var AUDIT_RUBBER = 0.32;
    var AUDIT_SPRING = 0.22;
    var AUDIT_THRESHOLD = 4;
    var AUDIT_DRAG_GAIN = 1.75; // wider audit columns need more travel per mouse pixel

    var auditEl = null;
    var auditPos = 0, auditTarget = 0, auditVel = 0;
    var auditStartX = 0, auditStartLeft = 0;
    var auditPressed = false, auditPointerId = null;
    var auditDragging = false, suppressAuditClick = false, auditRaf = null;
    var auditSource = null;
    var goldDrag = null;

    function auditMaxScroll(sc) {
      sc = sc || auditEl;
      return sc ? Math.max(0, sc.scrollWidth - sc.clientWidth) : 0;
    }

    function syncGoldBar(sc) {
      if (!sc) return;
      var shell = sc.closest('.nen-au-scroll-shell');
      if (!shell) return;
      var bar = shell.querySelector('.nen-au-goldbar');
      var thumb = shell.querySelector('.nen-au-goldthumb');
      var pos = shell.querySelector('.nen-au-goldpos');
      if (!bar || !thumb) return;
      var max = Math.max(0, sc.scrollWidth - sc.clientWidth);
      var bw = bar.clientWidth;
      if (!max || bw <= 0) {
        bar.style.display = 'none';
        if (pos) pos.style.display = 'none';
        return;
      }
      bar.style.display = '';
      if (pos) pos.style.display = '';
      var visibleRatio = Math.min(1, sc.clientWidth / sc.scrollWidth);
      var tw = Math.max(40, Math.round(bw * visibleRatio));
      var travel = Math.max(0, bw - tw);
      var ratio = max ? Math.max(0, Math.min(1, sc.scrollLeft / max)) : 0;
      var x = ratio * travel;
      thumb.style.width = tw + 'px';
      thumb.style.transform = 'translate3d(' + x + 'px,0,0)';
      if (pos) pos.textContent = Math.round(ratio * 100) + '%';
    }

    function syncAllGoldBars() {
      bodyEl.querySelectorAll('.nen-au-scroll').forEach(syncGoldBar);
    }

    function auditClaim(node) {
      if (node.dataset.nenAuRailClaimed) return;
      node.dataset.nenAuRailClaimed = '1';
      node.style.scrollSnapType = 'none';
      node.style.scrollBehavior = 'auto';
    }

    function auditStopMotion() {
      if (auditRaf) { cancelAnimationFrame(auditRaf); auditRaf = null; }
      if (auditEl) auditEl.classList.remove('nen-au-dragging');
      auditEl = null;
      auditDragging = false;
      auditVel = 0;
    }

    function auditEndPress() {
      auditPressed = false;
      auditPointerId = null;
      auditDragging = false;
      auditSource = null;
      if (auditEl) auditEl.classList.remove('nen-au-dragging');
    }

    // This is the SAME tick model as exchange-board-rails.js.
    function auditTick() {
      if (!auditEl) { auditRaf = null; return; }
      auditRaf = requestAnimationFrame(auditTick);
      var max = auditMaxScroll();

      if (auditDragging && auditPressed) {
        var t = auditTarget;
        if (t < 0) t = t * AUDIT_RUBBER;
        else if (t > max) t = max + (t - max) * AUDIT_RUBBER;
        var prev = auditPos;
        auditPos += (t - auditPos) * AUDIT_LERP;
        auditVel = auditVel * 0.62 + (auditPos - prev) * 0.38;
      } else if (auditPos < 0 || auditPos > max) {
        var bound = auditPos < 0 ? 0 : max;
        auditPos += (bound - auditPos) * AUDIT_SPRING;
        auditVel = 0;
        if (Math.abs(bound - auditPos) < 0.3) {
          auditEl.scrollLeft = bound;
          syncGoldBar(auditEl);
          auditStopMotion();
          return;
        }
      } else {
        auditPos += auditVel;
        auditVel *= AUDIT_FRICTION;
        if (auditPos < 0) { auditPos = 0; auditVel *= -0.28; }
        else if (auditPos > max) { auditPos = max; auditVel *= -0.28; }
        if (Math.abs(auditVel) < AUDIT_MIN_VEL) {
          auditEl.scrollLeft = auditPos;
          syncGoldBar(auditEl);
          auditStopMotion();
          return;
        }
      }

      auditEl.scrollLeft = auditPos;
      syncGoldBar(auditEl);
    }

    function auditRun() {
      if (!auditRaf) auditRaf = requestAnimationFrame(auditTick);
    }

    bodyEl.addEventListener('pointerdown', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      if (e.button !== 0 || !e.target || !e.target.closest) return;

      // Custom gold scrollbar: only below the price/date viewport.
      var bar = e.target.closest('.nen-au-goldbar');
      if (bar) {
        var shell = bar.closest('.nen-au-scroll-shell');
        var scBar = shell && shell.querySelector('.nen-au-scroll');
        var thumb = bar.querySelector('.nen-au-goldthumb');
        if (!scBar || !thumb) return;
        auditStopMotion();
        var br = bar.getBoundingClientRect();
        var tr = thumb.getBoundingClientRect();
        var grab = e.target.closest('.nen-au-goldthumb') ? (e.clientX - tr.left) : (tr.width / 2);
        goldDrag = { bar: bar, sc: scBar, pointerId: e.pointerId, grab: grab };
        try { bar.setPointerCapture(e.pointerId); } catch (_) {}
        if (!e.target.closest('.nen-au-goldthumb')) {
          var travel = Math.max(1, br.width - tr.width);
          var x = Math.max(0, Math.min(travel, e.clientX - br.left - grab));
          scBar.scrollLeft = (x / travel) * Math.max(0, scBar.scrollWidth - scBar.clientWidth);
          syncGoldBar(scBar);
        }
        e.preventDefault();
        return;
      }

      var sc = e.target.closest('.nen-au-scroll');
      if (!sc) return;
      // Buttons/links/selects stay controls. Inputs are intentionally allowed:
      // a click edits; crossing the same 4px threshold changes it into a drag.
      if (e.target.closest('button,select,textarea,a')) return;
      if (sc.scrollWidth <= sc.clientWidth + 2) return;

      auditClaim(sc);
      if (auditRaf) { cancelAnimationFrame(auditRaf); auditRaf = null; }
      if (auditEl && auditEl !== sc) auditEl.classList.remove('nen-au-dragging');

      auditEl = sc;
      auditPos = auditTarget = auditStartLeft = sc.scrollLeft;
      auditStartX = e.clientX;
      auditVel = 0;
      auditDragging = false;
      auditPressed = true;
      auditPointerId = e.pointerId;
      auditSource = e.target;
      try { sc.setPointerCapture(e.pointerId); } catch (_) {}
    });

    bodyEl.addEventListener('pointermove', function (e) {
      if (goldDrag) {
        if (goldDrag.pointerId != null && e.pointerId !== goldDrag.pointerId) return;
        var br = goldDrag.bar.getBoundingClientRect();
        var thumb = goldDrag.bar.querySelector('.nen-au-goldthumb');
        var tw = thumb ? thumb.getBoundingClientRect().width : 40;
        var travel = Math.max(1, br.width - tw);
        var x = Math.max(0, Math.min(travel, e.clientX - br.left - goldDrag.grab));
        goldDrag.sc.scrollLeft = (x / travel) * Math.max(0, goldDrag.sc.scrollWidth - goldDrag.sc.clientWidth);
        syncGoldBar(goldDrag.sc);
        e.preventDefault();
        return;
      }

      if (!auditPressed || !auditEl) return;
      if (auditPointerId != null && e.pointerId !== auditPointerId) return;
      if (!(e.buttons & 1)) { auditEndPress(); auditRun(); return; }

      var dx = e.clientX - auditStartX;

      if (!auditDragging) {
        if (Math.abs(dx) < AUDIT_THRESHOLD) return;
        auditDragging = true;
        auditStartLeft = auditPos;
        auditStartX = e.clientX;
        dx = 0;
        auditEl.classList.add('nen-au-dragging');
        // Preserve click-to-edit, but once this becomes a real drag the input
        // should stop selecting text/caret while the rail moves.
        var input = auditSource && auditSource.closest && auditSource.closest('input');
        if (input) input.blur();
        auditRun();
      }

      e.preventDefault();
      auditTarget = auditStartLeft - (dx * AUDIT_DRAG_GAIN);
    }, { passive: false });

    function endGoldDrag(e) {
      if (!goldDrag) return false;
      if (e && goldDrag.pointerId != null && e.pointerId !== goldDrag.pointerId) return true;
      try { goldDrag.bar.releasePointerCapture(goldDrag.pointerId); } catch (_) {}
      goldDrag = null;
      return true;
    }

    bodyEl.addEventListener('pointerup', function (e) {
      if (endGoldDrag(e)) return;
      if (!auditPressed) return;
      if (auditPointerId != null && e.pointerId !== auditPointerId) return;

      var wasDragging = auditDragging;
      try { if (auditEl) auditEl.releasePointerCapture(auditPointerId); } catch (_) {}
      auditEndPress();

      if (!wasDragging) { auditStopMotion(); return; }

      suppressAuditClick = true;
      setTimeout(function () { suppressAuditClick = false; }, 80);
      auditRun();
    });

    bodyEl.addEventListener('pointercancel', function (e) {
      if (endGoldDrag(e)) return;
      auditEndPress();
      auditStopMotion();
    });

    bodyEl.addEventListener('wheel', function(e) {
      if (e.target && e.target.closest && e.target.closest('.nen-au-scroll')) auditStopMotion();
    }, { passive: true });

    // Scroll does not bubble, so capture it to keep thumb + percentage synced.
    bodyEl.addEventListener('scroll', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('nen-au-scroll')) syncGoldBar(e.target);
    }, true);
    window.addEventListener('resize', function () { requestAnimationFrame(syncAllGoldBars); });
    requestAnimationFrame(syncAllGoldBars);

    // Swallow the click generated at the end of a real drag, same behavior as
    // the main rail engine, without blocking a genuine click into a price input.
    bodyEl.addEventListener('click', function (e) {
      if (!suppressAuditClick) return;
      suppressAuditClick = false;
      e.stopPropagation();
      e.preventDefault();
    }, true);

    bodyEl.addEventListener('dragstart', function (e) {
      if (auditDragging || (e.target && e.target.closest && e.target.closest('.nen-au-scroll'))) e.preventDefault();
    });

    bodyEl.addEventListener('click', function (e) {
      var ruleBtn = e.target.closest('.nen-au-rulebtn');
      if (ruleBtn) {
        var rowRule = lastRows[+ruleBtn.dataset.row];
        if (rowRule) openRuleView(rowRule);
        return;
      }

      var catalogBtn = e.target.closest('.nen-au-catalogbtn');
      if (catalogBtn) {
        var rowCatalog = lastRows[+catalogBtn.dataset.row];
        if (rowCatalog && isStaff) openCatalogView(rowCatalog);
        return;
      }

      // ── add: create this card on a date it never had ──
      var add = e.target.closest('.nen-au-add');
      if (add) {
        if (!isStaff) return;
        var rowA = lastRows[+add.dataset.row];
        var siA = +add.dataset.si;
        if (!rowA) return;
        addCell(rowA, siA);
        render();
        repaintBoard();
        return;
      }

      // ── delete one date ──
      var del = e.target.closest('.nen-au-del');
      if (del) {
        if (!isStaff) return;
        var rowD = lastRows[+del.dataset.row];
        var siD = +del.dataset.si;
        var sD = snaps()[siD];
        if (!rowD || !sD) return;
        if (!window.confirm('Eliminar "' + (rowD.name || rowD.sku) + '" de la snapshot ' +
                            sD.date + '?\n\nSe aplica al guardar en el bucket.')) return;
        deleteCell(rowD, siD);
        return;
      }

      // ── delete the whole card, every date, every field ──
      var delRow = e.target.closest('.nen-au-delrow');
      if (delRow) {
        if (!isStaff) return;
        var rowR = lastRows[+delRow.dataset.row];
        if (!rowR) return;
        var n = Object.keys(rowR.cells).length;
        if (!window.confirm(
          'Eliminar por completo:\n\n' +
          '  ' + (rowR.name || '(sin nombre)') + '\n' +
          '  ' + (rowR.sku || rowR.ticker || '') + '\n\n' +
          'Se borra la carta entera — precio, nombre, imagen y todos sus datos — ' +
          'de las ' + n + ' snapshot(s) donde aparece.\n\n' +
          'Ninguna otra carta se toca. Se aplica al guardar en el bucket.')) return;
        deleteRow(rowR);
        return;
      }

      // click the thumbnail to reveal that row's name + image fields
      var th = e.target.closest('.nen-au-th');
      if (!th) return;
      var box = bodyEl.querySelector('.nen-au-edit[data-editrow="' + th.dataset.row + '"]');
      if (!box) return;
      box.hidden = !box.hidden;
      if (!box.hidden) {
        var first = box.querySelector('input');
        if (first) first.focus();
      }
    });

    bodyEl.addEventListener('change', function (e) {
      // ── price: this snapshot only ──
      var px = e.target.closest('.nen-au-px');
      if (px) {
        var si = +px.dataset.si, i = +px.dataset.i;
        var s = snaps()[si];
        if (!s) return;
        var card = s.cards[i];
        if (!card) return;
        var n = parseFloat(String(px.value || '').replace(/,/g, ''));
        if (isNaN(n) || n < 0) { px.value = formatAuditPrice(card.price); return; }
        var key = s.date + SEP + i;
        if (!dirty.has(key)) dirty.set(key, card.price);   // keep the original
        card.price = n;
        saveMsg = null;
        render();
        repaintBoard();
        return;
      }

      // ── sku: every snapshot in the row ──
      // The sku is identity, not a measurement. Changing one date's copy
      // would fracture rowKey() and split the card in two, so it writes
      // across the row exactly like name and image do.
      var sk = e.target.closest('.nen-au-sku');
      if (sk) {
        var rowS = lastRows[+sk.dataset.row];
        if (!rowS) return;
        var newSku = sk.value.trim();
        if (!newSku) { sk.value = rowS.sku || ''; return; }   // never blank a sku
        if (newSku === (rowS.sku || '')) return;

        // Only the dates this row actually occupies are written to, so only
        // those can collide.
        var clash = null;
        snaps().forEach(function (s4, si4) {
          if (clash) return;
          var mine = rowS.cells[si4];
          if (!mine) return;
          var busy = (s4.cards || []).some(function (x) {
            return x !== mine && String(x.sku || '') === newSku;
          });
          if (busy) clash = s4.date;
        });
        if (clash) {
          window.alert('No se puede usar "' + newSku + '".\n\n' +
            'Otra carta ya tiene ese SKU en la snapshot ' + clash + '. ' +
            'Dos cartas con el mismo SKU se parten en todas las vistas.');
          sk.value = rowS.sku || '';
          return;
        }

        snaps().forEach(function (s4, si4) {
          var c4 = rowS.cells[si4];
          if (!c4) return;
          stashMeta(s4.date, c4);
          c4.sku = newSku;
        });
        rowS.sku = newSku;
        saveMsg = null;
        render();
        repaintBoard();
        return;
      }

      // ── name: every snapshot in the row ──
      var nm = e.target.closest('.nen-au-name');
      if (nm) {
        var rowN = lastRows[+nm.dataset.row];
        if (!rowN) return;
        var val = nm.value.trim();
        if (!val) { nm.value = rowN.name || ''; return; }   // never blank a name
        snaps().forEach(function (s2, si2) {
          var c2 = rowN.cells[si2];
          if (!c2) return;
          stashMeta(s2.date, c2);
          c2.name = val;
        });
        rowN.name = val;
        saveMsg = null;
        render();
        repaintBoard();
        return;
      }

      // ── image: every snapshot in the row ──
      var im = e.target.closest('.nen-au-img');
      if (im) {
        var rowI = lastRows[+im.dataset.row];
        if (!rowI) return;
        var url = im.value.trim();
        // the board renders this straight into src — anything but http(s) out
        if (url && !/^https?:\/\//i.test(url)) { im.value = rowI.image || ''; return; }
        snaps().forEach(function (s3, si3) {
          var c3 = rowI.cells[si3];
          if (!c3) return;
          stashMeta(s3.date, c3);
          c3.image = url;
        });
        rowI.image = url;
        saveMsg = null;
        render();
        repaintBoard();
      }
    });

    document.getElementById('nenAuRevert').onclick = function () {
      // Structural changes come out first, newest-first, so each splice undoes
      // exactly what the matching one did.
      var touched = {};

      for (var a = addedRows.length - 1; a >= 0; a--) {
        var rec = addedRows[a];
        var sa = snaps()[rec.si];
        if (!sa) continue;
        var at = sa.cards.indexOf(rec.card);
        if (at >= 0) sa.cards.splice(at, 1);
        touched[rec.si] = true;
      }
      addedRows.length = 0;

      for (var d = removedRows.length - 1; d >= 0; d--) {
        var rr = removedRows[d];
        var sd = snaps()[rr.si];
        if (!sd) continue;
        sd.cards.splice(Math.min(rr.pos, sd.cards.length), 0, rr.card);
        touched[rr.si] = true;
      }
      removedRows.length = 0;

      Object.keys(touched).forEach(function (k) { reindex(+k); });

      dirty.forEach(function (orig, key) {
        var p = key.split(SEP);
        var s = snaps().find(function (x) { return x.date === p[0]; });
        if (s && s.cards[+p[1]]) s.cards[+p[1]].price = orig;
      });
      dirtyMeta.forEach(function (orig, key) {
        var p = key.split(SEP);
        var s = snaps().find(function (x) { return x.date === p[0]; });
        if (s && s.cards[+p[1]]) {
          s.cards[+p[1]].name = orig.name;
          s.cards[+p[1]].image = orig.image;
          if (orig.sku != null) s.cards[+p[1]].sku = orig.sku;
        }
      });
      dirty.clear();
      dirtyMeta.clear();
      saveMsg = null;
      render();
      repaintBoard();
    };

    document.getElementById('nenAuSave').onclick = saveToBucket;

    document.getElementById('nenAuCsv').onclick = function () {
      exportSnapshot(+document.getElementById('nenAuSnap').value, 'csv');
    };
    document.getElementById('nenAuJson').onclick = function () {
      exportSnapshot(+document.getElementById('nenAuSnap').value, 'json');
    };
  }

  function openAudit(q) {
    buildModal();
    lastQuery = q || '';
    var qEl = document.getElementById('nenAuQ');
    if (qEl) qEl.value = lastQuery;
    render();
    document.getElementById('nenAuBackdrop').classList.add('open');
    setTimeout(function () { if (qEl && !lastQuery) qEl.focus(); }, 60);

    // The role check is a network round trip. Paint the read-only table first,
    // then bring the staff controls in when the server answers.
    confirmStaff().then(function (ok) { if (ok) render(); });
  }

  function closeAudit() {
    ruleViewSku = ''; ruleViewRow = null;
    var el = document.getElementById('nenAuBackdrop');
    if (el) el.classList.remove('open');
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAudit();
  });

  // Unsaved structural edits are the expensive kind to lose — a price you can
  // retype, a deletion you may not remember making.
  window.addEventListener('beforeunload', function (e) {
    if (!pendingCount()) return;
    e.preventDefault();
    e.returnValue = '';
  });

  window.nenAudit = openAudit;   // console access: nenAudit('EB03-003')

  // ── entry points ────────────────────────────────────────────
  function addToolbarBtn() {
    var host = document.querySelector('.exchange-toolbar .filters');
    if (!host || document.getElementById('nenAuOpen')) return;
    var b = document.createElement('button');
    b.id = 'nenAuOpen';
    b.type = 'button';
    b.className = 'btn';
    b.textContent = '🔎 Auditoría';
    b.onclick = function () { openAudit(''); };
    host.appendChild(b);
  }

  // per-card button, injected each time the drawer repaints
  function addDrawerBtn() {
    var left = document.querySelector('#cdBody .cd-left');
    if (!left || left.querySelector('.nen-au-open')) return;

    var tk = left.querySelector('.tk');
    if (!tk) return;
    // "TICKER · SKU" — the SKU is the more specific handle when present
    var parts = tk.textContent.split('·').map(function (x) { return x.trim(); });
    var q = parts[1] || parts[0] || '';

    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'nen-au-open';
    b.textContent = '🔎 Auditar precios';
    b.onclick = function (e) { e.stopPropagation(); openAudit(q); };

    // The snapshot pill only renders when the card was opened from a pinned
    // section. Sit beside it when it's there; otherwise build the same row so
    // the button lands in one consistent place.
    var row = left.querySelector('.cd-snapnote');
    if (!row) {
      row = document.createElement('div');
      row.className = 'cd-snapnote';
      var delta = left.querySelector('.cd-delta');
      if (delta && delta.parentNode) delta.parentNode.insertBefore(row, delta.nextSibling);
      else left.appendChild(row);
    }
    row.appendChild(b);
  }

  function boot() {
    addToolbarBtn();
    var body = document.getElementById('cdBody');
    if (body) new MutationObserver(addDrawerBtn).observe(body, { childList: true });
    addDrawerBtn();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 400); }, { once: true });
  } else {
    setTimeout(boot, 400);
  }
})();